import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useFeatureFlagVariantKey } from 'posthog-js/react';
import { useDispatch } from 'react-redux';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';

import { useAppSelector } from './hooks';
import { isMobileScreen } from './helper';
import { getMetaTags } from './store/V2/MetaTagsSlice/api';
import { getMetaTagsState } from './store/V2/MetaTagsSlice';
import RouteErrorBoundary from './components/ErrorBoundary/RouteErrorBoundary';

// Import lazy-loaded route components
import {
  LazyHome,
  LazyDashboard,
  LazyPortalPage,
  LazyPastPapersPage,
  LazyBlogPage,
  LazyManageAccounts,
  LazyWaitingRoomInit,
  LazySignUp,
  LazyLogin,
  LazyForgotPassword,
  LazyResetPassword,
  LazySubTopicListPage,
  LazyQuestionsPage,
  LazyTrialClassRecordings,
  LazyPayment,
  LazyManageSubscriptions,
  LazyInvalidRoute,
  LazyVerifyNotesLink,
  LazyStartSubmission,
  LazyReportIssue,
  LazyCurriculumPage,
  LazyStudentOnboarding,
  LazyQuickSignUp,
  LazyActionsPage,
} from './routes/LazyRoutes';
import { getAuthState } from './store/AuthSlice';
import { getEnrolmentCoursesForUsersForApp } from './store/V2/CoursesSlice';
import { getTenantsState, TUTOPIYA_TENANT_KEY } from './store/V2/TenantsSlice';

/**
 * Redirects /edexcel-igcse to the Next.js migrated resource page.
 *   /edexcel-igcse → /resource/edexcel-igcse
 */
const RedirectToNextEdexcelIgcse: React.FC = () => {
  React.useEffect(() => {
    window.location.replace(`${process.env.PUBLIC_URL}/resource/edexcel-igcse`);
  }, []);

  return null;
};

/**
 * Redirects /dashboard/edexcel-igcse/.../past-papers to the Next.js migrated resource page.
 *   /dashboard/edexcel-igcse/mathematics/higher-tier/4ma1/past-papers
 *     → /resource/edexcel-igcse/mathematics/higher-tier/4ma1/past-papers
 */
const RedirectToNextEdexcelIgcsePastPapers: React.FC = () => {
  React.useEffect(() => {
    const path = window.location.pathname.replace(
      `${process.env.PUBLIC_URL}/dashboard/edexcel-igcse`,
      `${process.env.PUBLIC_URL}/resource/edexcel-igcse`
    );
    window.location.replace(path);
  }, []);

  return null;
};

const BaseRoute: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { '*': curriculum } = useParams();

  // State
  const [isEnrolledCoursesInitiated, setIsEnrolledCoursesInitiated] = useState(
    false
  );

  // Store
  const metaTagState = useAppSelector(getMetaTagsState);
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);

  const { metaTagData } = metaTagState;
  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  const isTutopiyaTenant = appTenant?.key === TUTOPIYA_TENANT_KEY;
  const edexcelRedirectFlag = useFeatureFlagVariantKey(
    'next_edexcel_igcse_redirect'
  );
  const isEdexcelRedirectEnabled =
    isTutopiyaTenant && edexcelRedirectFlag === 'test';

  useEffect(() => {
    dispatch(
      getMetaTags({
        path: encodeURIComponent(location.pathname.replace(/[/]$/, '')),
      })
    );
  }, [location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (authUser && !isEnrolledCoursesInitiated) {
      dispatch(
        getEnrolmentCoursesForUsersForApp({
          limit: 200,
          offset: 0,
          enrolment: 'ENROLLED',
        })
      );

      setIsEnrolledCoursesInitiated(true);
    }
  }, [authUser]);

  const checkBaseOrCurriculumRoute = () => {
    if (!curriculum) {
      return true;
    }
    const segments = curriculum.split('/').filter(Boolean);
    if (segments.length === 1) {
      return true;
    }
    return false;
  };

  return (
    <>
      {metaTagData && (metaTagData.title || metaTagData.description) ? (
        <Helmet>
          {metaTagData.title ? <title>{metaTagData.title}</title> : null}
          {metaTagData.description ? (
            <meta name="description" content={metaTagData.description} />
          ) : null}
        </Helmet>
      ) : null}
      <Helmet>
        <link
          rel="canonical"
          href={`${window.location.origin}${location.pathname}`.replace(
            /\/$/,
            ''
          )}
        />
      </Helmet>
      <Routes>
        <Route
          path="/"
          element={
            <RouteErrorBoundary>
              <LazyHome />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/sign-up"
          element={
            <RouteErrorBoundary>
              <LazySignUp />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/login"
          element={
            <RouteErrorBoundary>
              <LazyLogin />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <RouteErrorBoundary>
              <LazyForgotPassword />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/reset-password"
          element={
            <RouteErrorBoundary>
              <LazyResetPassword />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/portal/*"
          element={
            <RouteErrorBoundary>
              <LazyPortalPage />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/trial-recording/:trialId"
          element={
            <RouteErrorBoundary>
              <LazyTrialClassRecordings />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/dashboard/*"
          element={
            <RouteErrorBoundary>
              <LazyDashboard />
            </RouteErrorBoundary>
          }
        />

        {isEdexcelRedirectEnabled ? (
          <Route
            path="/edexcel-igcse"
            element={<RedirectToNextEdexcelIgcse />}
          />
        ) : null}
        <Route
          path="/:curriculum"
          element={
            <RouteErrorBoundary>
              <LazyCurriculumPage />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/verifyNotesLink"
          element={
            <RouteErrorBoundary>
              <LazyVerifyNotesLink />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/startSubmission/:quizId"
          element={
            <RouteErrorBoundary>
              <LazyStartSubmission />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/payment/:diagnosticId"
          element={
            <RouteErrorBoundary>
              <LazyPayment />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/*"
          element={
            <RouteErrorBoundary>
              <LazyInvalidRoute />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/subscriptions"
          element={
            <RouteErrorBoundary>
              <LazyManageSubscriptions />
            </RouteErrorBoundary>
          }
        />

        <Route
          path="/:curriculum/:subject"
          element={
            <RouteErrorBoundary>
              <LazySubTopicListPage />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/:curriculum/:subject/*"
          element={
            <RouteErrorBoundary>
              <LazyQuestionsPage />
            </RouteErrorBoundary>
          }
        />
        {isEdexcelRedirectEnabled ? (
          <Route
            path="/dashboard/edexcel-igcse/:subject/:subjectLevel/:subjectCode/past-papers"
            element={<RedirectToNextEdexcelIgcsePastPapers />}
          />
        ) : null}
        <Route
          path="/dashboard/:curriculum/:subject/:subjectLevel/:subjectCode/past-papers"
          element={
            <RouteErrorBoundary>
              <LazyPastPapersPage />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/topical-past-papers"
          element={
            <RouteErrorBoundary>
              <LazyBlogPage />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/manage-accounts"
          element={
            <RouteErrorBoundary>
              <LazyManageAccounts />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/onboarding"
          element={
            <RouteErrorBoundary>
              <LazyStudentOnboarding />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/waiting-room/:waitingId"
          element={
            <RouteErrorBoundary>
              <LazyWaitingRoomInit />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/quick-sign-up"
          element={
            <RouteErrorBoundary>
              <LazyQuickSignUp />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/actions"
          element={
            <RouteErrorBoundary>
              <LazyActionsPage />
            </RouteErrorBoundary>
          }
        />
      </Routes>
      {!isMobileScreen() && !checkBaseOrCurriculumRoute() && (
        <RouteErrorBoundary>
          <LazyReportIssue />
        </RouteErrorBoundary>
      )}
    </>
  );
};

export default BaseRoute;
